/**
 * Localized-text handling for POSHUB settings.
 *
 * The QR App Layout Editor writes text fields either as a plain string
 * (legacy, still the bulk of the stored data) or as a per-locale object:
 *
 *   "Menu"                       // legacy
 *   { "nl": "Menu", "en": "Menu" } // bilingual
 *
 * getLocalizedText() normalises both shapes to a renderable string.
 * Plain strings are returned untouched, so existing settings keep working.
 */

const FALLBACK_LOCALES = ['nl', 'en'];

function isFilled(value) {
  return value !== null && value !== undefined && value !== '';
}

export function getLocalizedText(value, locale) {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    if (isFilled(locale) && isFilled(value[locale])) {
      return String(value[locale]);
    }

    for (let i = 0; i < FALLBACK_LOCALES.length; i += 1) {
      const candidate = value[FALLBACK_LOCALES[i]];
      if (isFilled(candidate)) {
        return String(candidate);
      }
    }

    const first = Object.keys(value)
      .map((key) => value[key])
      .find(isFilled);

    return isFilled(first) ? String(first) : '';
  }

  return String(value);
}

/**
 * An allergen carries its translations in `data` ({ nl, en }) alongside a plain
 * `name` that is always English. Prefer the localised value, but fall back to
 * `name` -- not every tenant has `data` populated.
 */
export function getAllergenName(allergen, locale) {
  if (!allergen) {
    return '';
  }
  const localised = getLocalizedText(allergen.data, locale);
  return localised || allergen.name || '';
}

/**
 * Catalogue text from POSHUB arrives as a base field plus a translation map:
 *
 *   name:              'Friet met schil'
 *   name_translations: { nl: 'Friet met schil', en: 'Skin-on fries' }
 *
 * The resource layer keys the map on every enabled locale and falls back to
 * the base text server-side, so map[locale] is always displayable -- an empty
 * value there means the field is genuinely empty, not missing. The only case
 * this has to handle is a payload carrying no map at all: a raw nested model
 * (product.category), or a backend predating the resource layer. Both render
 * the base text.
 *
 * Distinct from getLocalizedText(), which normalises a single Layout Editor
 * field that is EITHER a string or a map. Here the two live side by side.
 */
export function getTranslatedText(translations, base, locale) {
  if (
    translations
    && typeof translations === 'object'
    && isFilled(locale)
    && Object.prototype.hasOwnProperty.call(translations, locale)
  ) {
    const translated = translations[locale];
    return translated === null || translated === undefined ? '' : String(translated);
  }

  return base === null || base === undefined ? '' : String(base);
}

export default getLocalizedText;
